import React, { useMemo, useEffect } from 'react';
import {
  Box,
  Center,
  constants,
  DefaultPageTemplate,
  Title,
  ButtonWithLink,
  Detail,
} from '@thecb/components';
import AlertBar from '../../components/alert-bar';
import { Table } from '../../components/table';
import { formatPrice, formatDate, capitalize } from '../../utils/helpers';
import { DrawerHistoryFiltersForm } from './DrawerHistoryFiltersForm';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDrawerHistoriesFilterOptionsRequest } from '../../state/shared';
import { getDefaultFormSelectOption } from '../../utils/filterFormsHelpers';

const Header = ({ alertBar, alertBarActions }) => (
  <Box width="100%" padding="1rem 0">
    <Center maxWidth="85.714rem">
      {alertBar.alerts.length > 0 && (
        <Box padding="1rem 0">
          <AlertBar {...alertBar} {...alertBarActions} />
        </Box>
      )}
      <Title
        color={constants.colors.CHARADE_GREY}
        weight={constants.fontWeights.FONT_WEIGHT_BOLD}
      >
        Drawer History
      </Title>
    </Center>
  </Box>
);

const DrawerHistoryIndex = ({
  alertBars,
  actions,
  data,
  meta,
  forms,
  errors,
  clearSearchField,
  shouldClear,
  fetching,
  fetchData,
}) => {
  const dispatch = useDispatch();
  const { isLoadingDrawerHistory } = useSelector(
    (state) => state.drawerHistoryIndex,
  );
  const { agencies, locations, terminals, users } = useSelector(
    (state) => state.shared.tableFilterOptions,
  );
  useEffect(() => {
    dispatch(fetchDrawerHistoriesFilterOptionsRequest());
  }, []);
  const columns = useMemo(
    () => [
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--id"
            fontSize={'1rem'}
          >
            Cash Action ID
          </Detail>
        ),
        accessor: 'id',
        Cell: ({
          value,
          row: {
            original: { type },
          },
        }) => {
          const pathname = type === 'closeout' ? 'closeout' : 'cashaction';
          return (
            <ButtonWithLink
              url={`/pos/${pathname}/${value}`}
              variant="smallGhost"
              text={value}
              extraStyles={`margin: 0.5rem 0; text-align: left; min-width: 0px;`}
              textExtraStyles="font-size: 1rem;"
              contentOverride
            >
              <Detail fontSize={'1rem'} color={constants.colors.LUCKY_POINT}>
                {value}
              </Detail>
            </ButtonWithLink>
          );
        },
        width: 100,
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--batch-number"
            fontSize={'1rem'}
          >
            Batch Number
          </Detail>
        ),
        accessor: 'batch',
        Cell: ({ value }) => <Detail fontSize={'1rem'}>{value}</Detail>,
        width: 175,
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--report-type"
            fontSize={'1rem'}
          >
            Report Type
          </Detail>
        ),
        accessor: 'type',
        width: 100,
        Cell: ({ value }) => capitalize(value),
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--location"
            fontSize={'1rem'}
          >
            Location
          </Detail>
        ),
        accessor: 'location',
        width: 100,
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--user"
            fontSize={'1rem'}
          >
            User
          </Detail>
        ),
        accessor: 'cashier',
        width: 150,
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--date-processed"
            fontSize={'1rem'}
          >
            Date Processed
          </Detail>
        ),
        accessor: 'processed_date',
        width: 150,
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: (
          <Detail
            variant="small"
            data-testid="table-header-column--amount"
            fontSize={'1rem'}
          >
            Amount
          </Detail>
        ),
        accessor: 'amount',
        Cell: ({ value }) => (
          <Detail
            fontSize={'1rem'}
            weight={constants.fontWeights.FONT_WEIGHT_BOLD}
          >
            {formatPrice(value)}
          </Detail>
        ),
        width: 50,
      },
    ],
    [],
  );
  const content = (
    <Table
      errors={errors}
      columns={columns}
      data={data ?? []}
      loading={isLoadingDrawerHistory}
      pageCount={meta.pagesTotal}
      totalItems={meta.recordsTotal}
      fetchData={fetchData}
      labelForTotal="Records"
    >
      <DrawerHistoryFiltersForm
        {...forms.drawerHistoryFiltersForm}
        {...actions.forms.drawerHistoryFiltersForm}
        forms={forms}
        agencyOptions={[getDefaultFormSelectOption('agencies'), ...agencies]}
        locationOptions={[
          getDefaultFormSelectOption('locations'),
          ...locations,
        ]}
        terminalOptions={[
          getDefaultFormSelectOption('terminals'),
          ...terminals,
        ]}
        userOptions={[getDefaultFormSelectOption('users'), ...users]}
        showErrors={errors}
        shouldClear={shouldClear}
        clear={clearSearchField}
        fetchData={fetchData}
        loading={fetching}
      />
    </Table>
  );

  return (
    <DefaultPageTemplate
      header={
        <Header
          key="DrawerHistoryIndexHeader"
          alertBar={alertBars.drawerHistoryIndexAlertBars}
          alertBarActions={actions.alertBars.drawerHistoryIndexAlertBars}
        />
      }
      gutters="0"
      content={content}
      maxWidth="85.714rem"
    />
  );
};

export default DrawerHistoryIndex;
